import { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export type EntityType = 'insider' | 'politician';

export interface FollowedEntity {
  entity_type: EntityType;
  entity_key: string; // always lowercased
  created_at: string;
}

const FREE_LIMITS: Record<EntityType, number> = { insider: 5, politician: 5 };
const PRO_LIMITS: Record<EntityType, number> = { insider: 50, politician: 50 };

const ENTITY_LABEL: Record<EntityType, string> = {
  insider: 'insiders',
  politician: 'politicians',
};

export type FollowResult = 'followed' | 'unfollowed' | 'limit' | 'unauthenticated' | 'error';

interface FollowedEntitiesContextValue {
  followedEntities: FollowedEntity[];
  loading: boolean;
  isFollowing: (type: EntityType, key: string) => boolean;
  follow: (type: EntityType, key: string) => Promise<FollowResult>;
  unfollow: (type: EntityType, key: string) => Promise<FollowResult>;
  freeLimit: (type: EntityType) => number;
  countFor: (type: EntityType) => number;
}

const FollowedEntitiesContext = createContext<FollowedEntitiesContextValue | null>(null);

export const FollowedEntitiesProvider = ({ children }: { children: ReactNode }) => {
  const { user, isPro } = useAuth();
  const [followedEntities, setFollowedEntities] = useState<FollowedEntity[]>([]);
  const [loading, setLoading] = useState(false);
  const inFlight = useRef(false);

  const fetchFollowed = useCallback(async () => {
    if (!user) {
      setFollowedEntities([]);
      return;
    }
    if (inFlight.current) return;
    inFlight.current = true;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('user_followed_entities')
        .select('entity_type, entity_key, created_at')
        .eq('user_id', user.id);
      if (error) throw error;
      setFollowedEntities(
        (data || []).map((r) => ({
          entity_type: r.entity_type as EntityType,
          entity_key: (r.entity_key || '').toLowerCase(),
          created_at: r.created_at,
        }))
      );
    } catch (err) {
      console.error('Failed to fetch followed entities:', err);
    } finally {
      setLoading(false);
      inFlight.current = false;
    }
  }, [user]);

  useEffect(() => {
    fetchFollowed();
  }, [fetchFollowed]);

  const freeLimit = useCallback((type: EntityType) => FREE_LIMITS[type], []);

  const countFor = useCallback(
    (type: EntityType) => followedEntities.filter((e) => e.entity_type === type).length,
    [followedEntities]
  );

  const isFollowing = useCallback(
    (type: EntityType, key: string) => {
      const k = (key || '').toLowerCase();
      return followedEntities.some((e) => e.entity_type === type && e.entity_key === k);
    },
    [followedEntities]
  );

  const follow = useCallback(
    async (type: EntityType, key: string): Promise<FollowResult> => {
      if (!user) return 'unauthenticated';
      const k = (key || '').trim().toLowerCase();
      if (!k) return 'error';

      if (isFollowing(type, k)) return 'followed';

      const currentCount = countFor(type);
      const cap = isPro ? PRO_LIMITS[type] : FREE_LIMITS[type];
      if (currentCount >= cap) {
        if (!isPro) {
          toast(`You've followed ${FREE_LIMITS[type]} ${ENTITY_LABEL[type]}. Pro increases this to ${PRO_LIMITS[type]} each.`, {
            action: { label: 'Upgrade', onClick: () => { window.location.href = '/pricing'; } },
          });
        }
        return 'limit';
      }

      const optimisticRow: FollowedEntity = {
        entity_type: type,
        entity_key: k,
        created_at: new Date().toISOString(),
      };
      setFollowedEntities((prev) => [...prev, optimisticRow]);

      const { error } = await supabase
        .from('user_followed_entities')
        .insert({ user_id: user.id, entity_type: type, entity_key: k });

      if (error) {
        setFollowedEntities((prev) =>
          prev.filter((e) => !(e.entity_type === type && e.entity_key === k))
        );
        console.error('Failed to follow entity:', error);
        return 'error';
      }

      // Dual-write to legacy table for insiders only
      if (type === 'insider') {
        try {
          const { error: legacyErr } = await supabase
            .from('user_followed_insiders')
            .insert({ user_id: user.id, filer_name: key });
          if (legacyErr) {
            console.error('[dual-write] insider follow legacy insert failed:', legacyErr);
          }
        } catch (err) {
          console.error('[dual-write] insider follow legacy insert threw:', err);
        }
      }

      return 'followed';
    },
    [user, isPro, isFollowing, countFor]
  );

  const unfollow = useCallback(
    async (type: EntityType, key: string): Promise<FollowResult> => {
      if (!user) return 'unauthenticated';
      const k = (key || '').trim().toLowerCase();
      if (!k) return 'error';

      const prev = followedEntities;
      setFollowedEntities((cur) =>
        cur.filter((e) => !(e.entity_type === type && e.entity_key === k))
      );

      const { error } = await supabase
        .from('user_followed_entities')
        .delete()
        .eq('user_id', user.id)
        .eq('entity_type', type)
        .eq('entity_key', k);

      if (error) {
        setFollowedEntities(prev);
        console.error('Failed to unfollow entity:', error);
        return 'error';
      }

      if (type === 'insider') {
        try {
          const { error: legacyErr } = await supabase
            .from('user_followed_insiders')
            .delete()
            .eq('user_id', user.id)
            .ilike('filer_name', key);
          if (legacyErr) {
            console.error('[dual-write] insider unfollow legacy delete failed:', legacyErr);
          }
        } catch (err) {
          console.error('[dual-write] insider unfollow legacy delete threw:', err);
        }
      }

      return 'unfollowed';
    },
    [user, followedEntities]
  );

  return (
    <FollowedEntitiesContext.Provider
      value={{ followedEntities, loading, isFollowing, follow, unfollow, freeLimit, countFor }}
    >
      {children}
    </FollowedEntitiesContext.Provider>
  );
};

export const useFollowedEntitiesContext = () => {
  const ctx = useContext(FollowedEntitiesContext);
  if (!ctx) {
    throw new Error('useFollowedEntitiesContext must be used within FollowedEntitiesProvider');
  }
  return ctx;
};
